import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';

import { Coachmark } from './Coachmark';
import { SpotlightOverlay } from './SpotlightOverlay';
import type { TutorialStep } from './types';

interface TutorialControllerProps {
  steps: TutorialStep[];
  currentStep: number;
  onNext: () => void;
  onPrev: () => void;
  onSkip: () => void;
  marcusMessage?: string;
}

const DOM_IDLE_MS = 1000;
const ANIMATION_SETTLE_MS = 400;

function waitForElement(selector: string, settle = false): Promise<boolean> {
  return new Promise(resolve => {
    const resolveFound = (found: boolean) => {
      if (!found || !settle) {
        resolve(found);
        return;
      }
      setTimeout(() => resolve(true), ANIMATION_SETTLE_MS);
    };

    if (document.querySelector(selector)) {
      resolveFound(true);
      return;
    }

    let idleTimer: ReturnType<typeof setTimeout>;

    const domSettle = () => {
      observer.disconnect();
      const found = !!document.querySelector(selector);
      if (!found && import.meta.env.DEV) {
        console.warn(
          `[Tutorial] Element not found after page settled: "${selector}". ` +
            'Verify the target component renders a matching data-tutorial attribute.'
        );
      }
      resolveFound(found);
    };

    const resetIdle = () => {
      clearTimeout(idleTimer);
      idleTimer = setTimeout(domSettle, DOM_IDLE_MS);
    };

    const observer = new MutationObserver(() => {
      if (document.querySelector(selector)) {
        clearTimeout(idleTimer);
        observer.disconnect();
        resolveFound(true);
        return;
      }
      resetIdle();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    resetIdle();
  });
}

function isRouteMatch(route: string): boolean {
  const [pathname, queryString] = route.split('?');
  if (!window.location.pathname.endsWith(`/${pathname}`)) return false;
  if (!queryString) return true;
  const requiredParams = new URLSearchParams(queryString);
  const currentParams = new URLSearchParams(window.location.search);
  for (const [key, value] of requiredParams) {
    if (currentParams.get(key) !== value) return false;
  }
  return true;
}

export function TutorialController({
  steps,
  currentStep,
  onNext,
  onPrev,
  onSkip,
  marcusMessage,
}: TutorialControllerProps) {
  const navigate = useNavigate();
  const step = steps[currentStep];
  const [overlayDismissed, setOverlayDismissed] = useState(false);

  useEffect(() => {
    if (!step) return;
    let active = true;

    // eslint-disable-next-line react-hooks/set-state-in-effect -- reset on step change
    setOverlayDismissed(false);

    const run = async () => {
      if (step.route && !isRouteMatch(step.route)) {
        const [pathname, queryString] = step.route.split('?');
        navigate({
          pathname,
          search: queryString ? `?${queryString}` : undefined,
        });
      }

      const found = await waitForElement(step.targetSelector);
      if (!active) return;

      if (!found) {
        onNext();
        return;
      }

      step.onEnter?.();
    };

    run();

    return () => {
      active = false;
      step.onExit?.();
    };
  }, [step, navigate, onNext]);

  useEffect(() => {
    if (!step || step.overlay !== 'passthrough') return;
    const handler = (e: MouseEvent) => {
      if ((e.target as Element).closest(step.targetSelector)) {
        setOverlayDismissed(true);
      }
    };
    document.addEventListener('click', handler, true);
    return () => document.removeEventListener('click', handler, true);
  }, [step]);

  useEffect(() => {
    if (!step || step.advanceOn !== 'click-target') return;

    const el = document.querySelector(step.targetSelector);
    if (!el) return;

    const handler = () => onNext();
    el.addEventListener('click', handler);
    return () => el.removeEventListener('click', handler);
  }, [step, onNext]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onSkip();
      }
    };
    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [onSkip]);

  if (!step) return null;

  const showOverlay = step.placement !== 'center' && !overlayDismissed;

  return (
    <>
      <SpotlightOverlay
        targetSelector={step.targetSelector}
        padding={step.spotlightPadding}
        unblurSelectors={step.unblurSelectors}
        isVisible={showOverlay}
        transition={step.transition}
        overlay={step.overlay}
        stepKey={step.id}
      />
      <Coachmark
        step={step}
        stepIndex={currentStep}
        totalSteps={steps.length}
        onNext={onNext}
        onPrev={onPrev}
        onSkip={onSkip}
        isVisible
        marcusMessage={marcusMessage}
      />
    </>
  );
}
